import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import Swal from 'sweetalert2';
import { PetsService } from '../../../../services/pets';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { DeviceDetectorService } from '../../../../services/device-detector.service';
import { UserService } from '../../../../services/user';

@Component({
  selector: 'app-pets',
  templateUrl: './pets.component.html',
  styleUrls: ['./pets.component.scss']
})
export class PetsComponent implements OnInit, AfterViewInit {

  warehouseId!: number;
  clientId?: number;

  allData: any[] = [];
  dataSource: MatTableDataSource<any> = new MatTableDataSource<any>([]);

  displayedColumns: string[] = [];

  contentReady = true;
  noData = false;

  petForm!: FormGroup;
  editMode = false;
  selectedPetId: number | null = null;

  isMobile!: boolean;
  isSubAdmin = false;

  searchKey = '';
  searched = false;

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  constructor(
    private petsService: PetsService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private deviceService: DeviceDetectorService,
    private router: Router,
    private transloco: TranslocoService,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadUserRole();

    this.isMobile = this.deviceService.isMobileDevice();

    if (this.isMobile) {
      this.router.navigateByUrl('/pets.mobile');
      return;
    }

    this.warehouseId = Number(localStorage.getItem('posId'));

    this.route.queryParams.subscribe(params => {
      if (params['id']) {
        this.clientId = +params['id'];
      } else {
        this.clientId = undefined;
      }

      this.setDisplayedColumns();
      this.loadPets();
    });
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  private loadUserRole(): void {
    this.userService.getAccountType().subscribe({
      next: (data) => {
        this.isSubAdmin = String(data?.user_type) === '6';
      },
      error: () => {
        this.isSubAdmin = false;
      }
    });
  }

  initForm(): void {
    this.petForm = this.fb.group({
      pet_type: ['', Validators.required],
      name: ['', Validators.required],
      breed: [''],
      color: [''],
      special_markings: [''],
      date_of_birth: [''],
      gender: ['male'],
      spayed_neutered: [false],
    });
  }

  setDisplayedColumns(): void {
    const columns = [
      'pet_type',
      'name',
      'breed',
      'color',
      'gender',
      'date_of_birth',
      'spayed_neutered'
    ];

    if (!this.clientId) {
      columns.push('client');
    }

    columns.push('actions');

    this.displayedColumns = columns;
  }

  loadPets(): void {
    this.contentReady = true;

    this.petsService.getAllPets(this.warehouseId, this.clientId).subscribe({
      next: (data) => {
        const raw = data.data || data;

        this.allData = (raw || []).map((p: any) => ({
          ...p,
          hasUpcomingVaccination: p?.hasUpcomingVaccination ?? false
        }));

        this.dataSource = new MatTableDataSource(this.allData);
        this.dataSource.filterPredicate = this.createFilterPredicate();

        setTimeout(() => {
          this.dataSource.paginator = this.paginator;
        });

        this.contentReady = false;
        this.noData = this.allData.length === 0;

        this.markPetsWithUpcomingVaccinations();

        if (this.searchKey.trim()) {
          this.search();
        }
      },

      error: (err) => {
        console.error(err);
        this.contentReady = false;
        this.noData = true;
      }
    });
  }

  savePet(): void {
    if (this.petForm.invalid) return;

    const payload: any = {
      ...this.petForm.value,
      warehouse_id: this.warehouseId
    };

    if (this.clientId) {
      payload.client_id = this.clientId;
    }

    if (this.editMode && this.selectedPetId) {
      this.petsService.updatePet(this.selectedPetId, payload).subscribe({
        next: () => {
          Swal.fire({
            icon: 'success',
            title: 'تم التحديث بنجاح',
            text: 'تم تعديل بيانات الحيوان بنجاح!'
          });

          this.resetForm();
          this.loadPets();
        },

        error: (err) => {
          console.error(err);
          Swal.fire('خطأ', 'حدث خطأ أثناء تعديل البيانات.', 'error');
        }
      });

      return;
    }

    this.petsService.createPet(payload).subscribe({
      next: () => {
        Swal.fire({
          icon: 'success',
          title: 'تم الحفظ بنجاح',
          text: 'تمت إضافة الحيوان بنجاح!'
        });

        this.resetForm();
        this.loadPets();
      },

      error: (err) => {
        console.error(err);
        Swal.fire('خطأ', 'تعذر إضافة الحيوان، حاول مرة أخرى.', 'error');
      }
    });
  }

  editPet(pet: any): void {
    this.editMode = true;
    this.selectedPetId = pet.id;

    this.petForm.patchValue({
      pet_type: pet.pet_type ?? '',
      name: pet.name ?? '',
      breed: pet.breed ?? '',
      color: pet.color ?? '',
      special_markings: pet.special_markings ?? '',
      date_of_birth: pet.date_of_birth ?? '',
      gender: pet.gender ?? 'male',
      spayed_neutered: this.isYes(pet.spayed_neutered),
    });

    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  deletePet(id: number): void {
    Swal.fire({
      title: 'هل أنت متأكد؟',
      text: 'لن يمكنك التراجع بعد الحذف!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'نعم، احذف!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.petsService.deletePet(id).subscribe({
          next: () => {
            this.loadPets();
            Swal.fire('تم الحذف!', 'تم حذف الحيوان بنجاح.', 'success');
          },

          error: (err) => {
            console.error(err);
            Swal.fire('خطأ', 'حدث خطأ أثناء حذف الحيوان.', 'error');
          }
        });
      }
    });
  }

  vaccinations(petId: number): void {
    this.router.navigate(['/petvaccinations'], { queryParams: { petId } });
  }

  markPetsWithUpcomingVaccinations(): void {
    const warehouseId = Number(localStorage.getItem('posId'));
    if (!warehouseId) return;

    this.petsService.getUpcomingVaccinations(warehouseId).subscribe({
      next: (res) => {
        const upcoming = res.data || res || [];
        const ids = new Set(upcoming.map((v: any) => v.pet_id));

        this.allData = this.allData.map((pet: any) => ({
          ...pet,
          hasUpcomingVaccination: ids.has(pet.id)
        }));

        this.dataSource.data = this.allData;

        if (this.searchKey.trim()) {
          this.search();
        }
      },

      error: (err) => console.error(err)
    });
  }

  search(): void {
    const filterValue = this.searchKey.trim().toLowerCase();

    this.searched = filterValue.length > 0;
    this.dataSource.filter = filterValue;

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }

    this.noData = this.dataSource.filteredData.length === 0;
  }

  onEnterPressed(): void {
    this.search();
  }

  clearSearch(): void {
    this.searchKey = '';
    this.searched = false;
    this.dataSource.filter = '';
    this.noData = this.allData.length === 0;

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  applyFilter(event: Event): void {
    this.searchKey = (event.target as HTMLInputElement).value;
    this.search();
  }

  resetForm(): void {
    this.petForm.reset({
      pet_type: '',
      name: '',
      breed: '',
      color: '',
      special_markings: '',
      date_of_birth: '',
      gender: 'male',
      spayed_neutered: false
    });

    this.editMode = false;
    this.selectedPetId = null;
  }

  createFilterPredicate(): (data: any, filter: string) => boolean {
    return (data: any, filter: string): boolean => {
      const clientName = data?.client?.client_name || '';
      const clientPhone = data?.client?.phone_number || '';

      const searchable = [
        data?.name,
        data?.breed,
        data?.color,
        data?.special_markings,
        data?.date_of_birth,
        data?.gender,
        this.getPetTypeLabel(data?.pet_type),
        clientName,
        clientPhone
      ]
        .join(' ')
        .toLowerCase();

      return searchable.includes(filter);
    };
  }

  getPetTypeLabel(petType: string | number): string {
    const labels: Record<string, string> = {
      '0': this.transloco.translate('cats'),
      '1': this.transloco.translate('dogs'),
      '2': this.transloco.translate('birds'),
      '4': this.transloco.translate('cats&dogs'),
      '3': this.transloco.translate('other')
    };

    return labels[petType?.toString()] || '—';
  }

  getPetTypeClass(petType: string | number): string {
    const classes: Record<string, string> = {
      '0': 'cats',
      '1': 'dogs',
      '2': 'birds',
      '4': 'cats&dogs',
      '3': 'other'
    };

    return classes[petType?.toString()] || 'other';
  }

  isYes(value: any): boolean {
    return value === true || value === 1 || value === '1';
  }
}
